import { useEffect, useState } from 'react';
import { isNextEnabled, isPreviousEnabled } from '../../utils';
import type { ICommonPaginationMapperProps } from '../../Pagination.types';

interface UseFocusNavButtonsOptions<Element>
  extends Pick<
    ICommonPaginationMapperProps,
    | 'forcePreviousButtonEnabled'
    | 'forceNextButtonEnabled'
    | 'isDisabled'
    | 'currentPage'
    | 'totalPages'
  > {
  nextButtonRef: React.RefObject<Element>;
  previousButtonRef: React.RefObject<Element>;
}

export const useFocusNavButtons = <Element extends HTMLElement>(
  options: UseFocusNavButtonsOptions<Element>,
) => {
  const {
    forcePreviousButtonEnabled,
    forceNextButtonEnabled,
    isDisabled,
    currentPage,
    totalPages,

    nextButtonRef,
    previousButtonRef,
  } = options;

  const [shouldFocusNavigationButtons, setShouldFocusNavigationButtons] =
    useState(false);

  const isPreviousButtonEnabled = isPreviousEnabled({
    forcePreviousButtonEnabled,
    isDisabled,
    currentPage,
  });
  const isNextButtonEnabled = isNextEnabled({
    forceNextButtonEnabled,
    isDisabled,
    currentPage,
    totalPages,
  });

  useEffect(() => {
    if (shouldFocusNavigationButtons) {
      if (isNextButtonEnabled && !isPreviousButtonEnabled) {
        nextButtonRef?.current?.focus();
      }

      if (isPreviousButtonEnabled && !isNextButtonEnabled) {
        previousButtonRef?.current?.focus();
      }

      setShouldFocusNavigationButtons(false);
    }
  }, [
    isNextButtonEnabled,
    isPreviousButtonEnabled,
    shouldFocusNavigationButtons,
    nextButtonRef,
    previousButtonRef,
  ]);

  return {
    isPreviousButtonEnabled,
    isNextButtonEnabled,
    setShouldFocusNavigationButtons,
  };
};
